<template>
  <g :class="cssClass" ref="gAxis" />
</template>

<script>
import utils from '@/utils';
import * as d3 from 'd3';

export default {
  props: {
    data: {
      type: [Array, Number],
      required: true
    },
    position: {
      type: String,
      required: true
    },
    scale: {
      type: Function,
      required: true
    },
    scaleType: {
      type: String,
      required: false
    },
    size: {
      type: Object,
      required: true
    },
    transition: {
      type: Object,
      required: false,
      default() {
        return {
          duration: 500,
          ease: d3.easeQuad
        };
      }
    },
    type: {
      type: String,
      required: false,
      default: 'default'
    },
    unit: {
      type: String,
      required: false,
      default: ''
    },
    isDrawed: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      el: {
        gAxis: ''
      },
      margin: 10
    };
  },
  computed: {
    cssClass() {
      if (this.isAxisX) {
        return 'c-graph-axis__x';
      } else {
        return 'c-graph-axis__y';
      }
    },
    isArray() {
      return Array.isArray(this.data);
    },
    isAxisX() {
      return this.position === 'bottom';
    },
    isAxisYR() {
      return this.position === 'right';
    },
    isComparison() {
      return this.type === 'comparison';
    }
  },
  mounted() {
    this.setEl();
    if (this.isDrawed) {
      this.draw();
    }
  },
  methods: {
    setEl() {
      this.el.gAxis = d3.select(this.$refs.gAxis);
    },
    draw() {
      this.$nextTick(() => {
        if (this.isAxisX) {
          this.drawAxisX();
        } else {
          this.drawAxisY();
        }
      });
    },
    drawAxisX() {
      let axis = d3.axisBottom(this.scale).tickSizeInner(0);

      if (this.isComparison) {
        axis.ticks(this.data.length);
      } else if (this.isArray) {
        axis.tickValues(this.data);
      } else {
        axis.ticks(this.data);
      }

      axis.tickFormat((d, i, nodes) => {
        if (this.isComparison) {
          d = this.data[i];
        }
        if (this.scaleType === 'number') {
          return this.$options.filters.separateNumber(d) + this.unit;
        } else {
          let format = 'YYYY';
          if (this.scaleType === 'timeDay' || this.scaleType === 'timeWeek') {
            format = 'DD';
          } else if (this.scaleType === 'timeMonth') {
            let el = d3.select(nodes[i]);
            let elPrev = d3.select(nodes[i - 1]);

            let year = String(d.getFullYear());
            el.attr('data-year', year);

            // 年が前の要素と異なる場合は年を表示する
            if (i === 0 || elPrev.attr('data-year') !== year) {
              format = 'YYYY.MM';
            } else {
              format = 'MM';
            }
          }
          return this.$options.filters.dateText(d, format);
        }
      });

      this.el.gAxis
        .attr(
          'transform',
          `translate(0, ${this.size.canvasH -
            this.size.axisXH -
            this.size.offset.bottom +
            6})`
        )
        .attr('opacity', 0)
        .call(axis)
        .interrupt()
        .transition()
        .duration(this.transition.duration)
        .ease(this.transition.ease)
        .attr('opacity', 1);

      if (this.scaleType === 'timeDay' || this.scaleType === 'timeWeek') {
        this.el.gAxis.selectAll('text').each((d, i, nodes) => {
          let el = d3.select(nodes[i]);
          let elPrev = d3.select(nodes[i - 1]);

          let year = String(d.getFullYear());
          let month = String(d.getMonth() + 1);
          el.attr('data-year', year).attr('data-month', month);

          // 年月が前の要素と異なる場合は年月を表示する
          if (i === 0 || elPrev.attr('data-month') !== month) {
            let format = 'MM';
            if (i === 0 || elPrev.attr('data-year') !== year) {
              format = 'YYYY.MM';
            }
            el.append('tspan')
              .text(this.$options.filters.dateText(d, format))
              .attr('x', 0)
              .attr('dy', '19');
          }
        });
      }
    },
    drawAxisY() {
      let axis, pos;

      if (this.isAxisYR) {
        axis = d3.axisLeft(this.scale);
        pos = this.size.canvasW;
      } else {
        axis = d3.axisRight(this.scale);
        pos = this.size.offset.left;
      }
      axis.tickSizeInner(10);

      if (this.isArray) {
        axis.tickValues(this.data);
      } else {
        if (this.scaleType === 'time') {
          // 60分間隔
          axis.ticks(this.data, 60);
        } else {
          axis.ticks(this.data);
        }
      }

      axis.tickFormat((d, i) => {
        if (this.scaleType === 'number') {
          return this.$options.filters.separateNumber(d);
        } else if (this.scaleType === 'time') {
          return this.$options.filters.secondsToClock(d);
        } else {
          return d;
        }
      });

      this.el.gAxis
        .attr('transform', `translate(${pos}, 0)`)
        .attr('opacity', 0)
        .call(axis)
        .interrupt()
        .transition()
        .duration(this.transition.duration)
        .ease(this.transition.ease)
        .attr('opacity', 1);

      // 最後の目盛りは表示しない
      this.el.gAxis.selectAll('g').attr('opacity', (d, i, nodes) => {
        let translate = utils.getTranslateValue(
          nodes[i].getAttribute('transform')
        );
        // 順位の0位は表示しない
        if (
          (this.scaleType === 'rank' && d === 0) ||
          translate[1] > this.size.graph.bottom - this.margin
        ) {
          return 0;
        }
        return 1;
      });

      this.el.gAxis
        .selectAll('text')
        .attr('text-anchor', () => {
          if (this.isAxisYR) {
            return 'end';
          } else {
            return 'start';
          }
        })
        .attr('transform', () => {
          if (this.isAxisYR) {
            return 'translate(13, 16)';
          } else {
            return 'translate(-13, 16)';
          }
        });

      if (this.scaleType === 'rank') {
        this.el.gAxis.selectAll('text').each((d, i, nodes) => {
          let el = d3.select(nodes[i]);
          el.append('tspan')
            .text('位')
            .attr('class', 'c-text--normal')
            .attr('dx', 1)
            .attr('dy', 0);
        });
      }
    },
    resize() {
      if (!this.isDrawed) {
        return;
      }
      this.$nextTick(() => {
        if (this.isAxisX) {
          this.drawAxisX();
        } else if (this.isAxisYR) {
          this.el.gAxis.attr('transform', `translate(${this.size.canvasW}, 0)`);
        }
      });
    }
  }
};
</script>
